import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useSite } from '../context/SiteContext';
import { useT } from '../context/I18nContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Search, ShoppingBag, User, Phone, Mail, Menu as MenuIcon, X, Instagram, Facebook, MessageCircle, Send, Music2, LogOut } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from './ui/sheet';
import { Input } from './ui/input';
import LanguageSwitcher from './LanguageSwitcher';

const SocialIcon = ({ Icon, href, label }) => (
  href ? <a href={href} target="_blank" rel="noreferrer" aria-label={label} className="text-zinc-300 hover:text-[#d4af37] transition-colors"><Icon className="h-4 w-4" /></a> : null
);

const Header = () => {
  const { count } = useCart();
  const { site, pages } = useSite();
  const { t } = useT();
  const { user: customerUser, logout: customerLogout } = useCustomerAuth();
  const [search, setSearch] = useState(false);
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setSearch(false); setMenu(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const brand = site?.brand || {};
  const socials = site?.socials || {};
  const customMenu = site?.header_menu || [];
  const headerPages = (pages || []).filter((p) => p.show_in_header);
  const annBar = site?.announcement_bar_enabled && site?.announcement_bar;

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b border-zinc-900">
      {annBar && (
        <div className="bg-[#d4af37] text-black text-center text-xs py-2 font-medium tracking-wide">{site.announcement_bar}</div>
      )}
      <div className="max-w-[1480px] mx-auto px-6 h-[88px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.site_name || 'Logo'}
              className="h-12 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 0 12px rgba(212,175,55,0.55)) drop-shadow(0 0 6px rgba(212,175,55,0.35))' }}
            />
          ) : (
            <>
              <div className="w-14 h-14 rounded-md border-2 border-[#d4af37] flex items-center justify-center bg-black">
                <span className="serif-italic text-[#d4af37] text-xl leading-none">WS</span>
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="serif text-[#d4af37] text-sm tracking-[0.25em]">{(brand.site_name || 'WE SUPPLY SMOKE').split(' ').slice(0, -1).join(' ').toUpperCase() || 'WE SUPPLY'}</div>
                <div className="serif text-[#d4af37] text-sm tracking-[0.25em]">{(brand.site_name || 'WE SUPPLY SMOKE').split(' ').slice(-1)[0].toUpperCase()}</div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {customMenu.length > 0 ? (
            // Admin has provided a custom header menu → use ONLY that (no defaults)
            customMenu.map((m) => (
              <a key={`${m.label}|${m.href}`} href={m.href} target={m.target} className="text-zinc-200 hover:text-[#d4af37] transition-colors text-[15px]">{m.label}</a>
            ))
          ) : (
            // No custom menu → show sensible defaults
            <>
              <Link to="/" className="text-zinc-200 hover:text-[#d4af37] transition-colors text-[15px]">{t('nav.home')}</Link>
              <Link to="/shop" className="text-zinc-200 hover:text-[#d4af37] transition-colors text-[15px]">{t('nav.shop')}</Link>
              {headerPages.map((p) => (
                <Link key={p.id} to={`/p/${p.slug}`} className="text-zinc-200 hover:text-[#d4af37] transition-colors text-[15px]">{p.title}</Link>
              ))}
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <div className="hidden md:flex items-center gap-3">
            <SocialIcon Icon={Facebook} href={socials.facebook} label="Facebook" />
            <SocialIcon Icon={Instagram} href={socials.instagram} label="Instagram" />
            <SocialIcon Icon={MessageCircle} href={socials.snapchat} label="Snapchat" />
            <SocialIcon Icon={Send} href={socials.telegram} label="Telegram" />
            <SocialIcon Icon={Music2} href={socials.tiktok} label="TikTok" />
          </div>
          <button onClick={() => setSearch(true)} aria-label="Search" className="text-zinc-300 hover:text-[#d4af37] transition-colors">
            <Search className="h-5 w-5" />
          </button>
          {site?.contact?.phone && <a href={`tel:${site.contact.phone}`} aria-label="Phone" className="hidden md:inline-block text-zinc-300 hover:text-[#d4af37] transition-colors"><Phone className="h-5 w-5" /></a>}
          {site?.contact?.email && <a href={`mailto:${site.contact.email}`} aria-label="Email" className="hidden md:inline-block text-zinc-300 hover:text-[#d4af37] transition-colors"><Mail className="h-5 w-5" /></a>}
          {customerUser ? (
            <div className="hidden md:flex items-center gap-2">
              <span className="text-zinc-400 text-sm" data-testid="header-customer-name">{customerUser.name?.split(' ')[0]}</span>
              <button onClick={() => { customerLogout(); nav('/'); }} data-testid="header-customer-logout" className="text-zinc-300 hover:text-rose-400 transition-colors" aria-label={t('account.logout')}>
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-customer-login" aria-label={t('account.sign_in')} className="hidden md:inline-block text-zinc-300 hover:text-[#d4af37] transition-colors">
              <User className="h-5 w-5" />
            </Link>
          )}
          <button onClick={() => nav('/cart')} aria-label="Cart" className="relative text-zinc-300 hover:text-[#d4af37] transition-colors">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (<span className="absolute -top-2 -right-2 bg-[#d4af37] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>)}
          </button>
          <button onClick={() => setMenu(true)} className="lg:hidden text-zinc-300" aria-label="Menu"><MenuIcon className="h-6 w-6" /></button>
        </div>
      </div>

      {search && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={() => setSearch(false)}>
          <button className="absolute top-6 right-6 text-zinc-300 hover:text-[#d4af37]" onClick={() => setSearch(false)} aria-label="Close"><X className="h-7 w-7" /></button>
          <div className="w-full max-w-2xl px-6" onClick={(e) => e.stopPropagation()}>
            <Input autoFocus placeholder={t('nav.search_placeholder')} className="bg-transparent border-0 border-b-2 border-[#d4af37] rounded-none text-2xl serif-italic text-[#d4af37] placeholder:text-zinc-500 focus-visible:ring-0 focus-visible:border-[#f5c518] h-14" />
            <p className="mt-3 text-xs text-zinc-500">Press ESC to close</p>
          </div>
        </div>
      )}

      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetTrigger asChild><span /></SheetTrigger>
        <SheetContent side="right" className="bg-black border-zinc-900 text-white w-[300px]">
          <SheetHeader><SheetTitle className="serif-italic gold-text">Menu</SheetTitle></SheetHeader>
          <nav className="mt-8 flex flex-col gap-5 text-lg">
            {customMenu.length > 0 ? (
              customMenu.map((m) => <a key={`${m.label}|${m.href}`} href={m.href} target={m.target} onClick={() => setMenu(false)} className="hover:text-[#d4af37]">{m.label}</a>)
            ) : (
              <>
                <Link onClick={() => setMenu(false)} to="/" className="hover:text-[#d4af37]">{t('nav.home')}</Link>
                <Link onClick={() => setMenu(false)} to="/shop" className="hover:text-[#d4af37]">{t('nav.shop')}</Link>
                {headerPages.map((p) => <Link key={p.id} onClick={() => setMenu(false)} to={`/p/${p.slug}`} className="hover:text-[#d4af37]">{p.title}</Link>)}
              </>
            )}
            <Link onClick={() => setMenu(false)} to="/cart" className="hover:text-[#d4af37]">{t('cart.title')}</Link>
            <div className="pt-3 border-t border-zinc-900 mt-3">
              <LanguageSwitcher />
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
